import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { MenuToggle, Search, ShoppingCart, User } from "./icons";
import { useAuth } from "../context/AuthContext";
import { useRFQ } from "../context/RFQContext";
import { useLanguage } from "../context/LanguageContext";
import BarcraftLogo from "./BarcraftLogo";
import TopBar from "./TopBar";
import LanguagePicker from "./LanguagePicker";

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSearchOpen, setSearchOpen] = useState(false);
  const [isMobileOpen, setMobileOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchRef = useRef<HTMLDivElement>(null);
  const { items, openDrawer } = useRFQ();
  const { user } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const hasDarkHero =
    pathname === "/" ||
    pathname === "/login" ||
    pathname === "/reset-password" ||
    pathname === "/verify-email" ||
    pathname === "/catalog";

  const NAV_LINKS = [
    { label: t("catalog"), href: "/catalog" },
    { label: t("about"), href: "/#about" },
    { label: t("portfolio"), href: "/#portfolio" },
    { label: t("services"), href: "/#services" },
  ];

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = searchQuery.trim();
    if (!trimmed) return;
    navigate(`/catalog?q=${encodeURIComponent(trimmed)}`);
    setSearchQuery("");
    setSearchOpen(false);
  };

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!isSearchOpen) return;

    const onClickOutside = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node) && !searchQuery.trim()) {
        setSearchOpen(false);
      }
    };

    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [isSearchOpen, searchQuery]);

  const solid = !hasDarkHero || isScrolled || isMobileOpen;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 border-b transition-all duration-300 ease-engineered ${
        solid
          ? "border-steel-200 bg-white/95 backdrop-blur"
          : "border-white/10 bg-transparent"
      }`}
    >
      <div
        className={`grid transition-[grid-template-rows] duration-300 ease-engineered ${
          isScrolled ? "grid-rows-[0fr]" : "grid-rows-[1fr]"
        }`}
      >
        <div className={`overflow-hidden transition-opacity duration-300 ease-engineered ${isScrolled ? "opacity-0" : "opacity-100"}`}>
          <TopBar />
        </div>
      </div>
      <div className="container-industrial flex h-20 items-center justify-between">
        {/* Plain <a>, not <Link> — a full reload back to "/" so the logo
            always lands at the top of a fresh page, including when it's
            clicked from the homepage itself (a same-path <Link> click is a
            no-op in React Router, so ScrollToTop's pathname-change effect
            never fires and the page stays right where it was scrolled). */}
        <a href="/" className={`flex items-center ${solid ? "text-ink" : "text-white"}`}>
          <BarcraftLogo className="h-[37px] w-auto" />
        </a>

        <nav className="hidden items-center gap-9 lg:flex">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={`font-sans text-[11px] font-semibold uppercase tracking-wide transition-colors duration-200 ease-engineered hover:text-accent ${
                solid ? "text-steel-600" : "text-white/80"
              }`}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/contacts"
            className={`font-sans text-[11px] font-semibold uppercase tracking-wide transition-colors duration-200 ease-engineered hover:text-accent ${
              solid ? "text-steel-600" : "text-white/80"
            }`}
          >
            {t("contacts")}
          </Link>
          <LanguagePicker solid={solid} />
        </nav>

        <div className="flex items-center gap-3">
          <div ref={searchRef} className="hidden items-center md:flex">
            <form
              onSubmit={handleSearchSubmit}
              className={`flex items-center overflow-hidden border-b transition-all duration-300 ease-engineered ${
                isSearchOpen
                  ? `w-56 px-1 ${solid ? "border-steel-300" : "border-white/40"}`
                  : "w-12 justify-center border-transparent px-0"
              } h-12`}
            >
              <button
                type="button"
                aria-label={t("searchToggle")}
                onClick={() => setSearchOpen((v) => !v)}
                className={`flex h-12 w-12 shrink-0 items-center justify-center transition-colors duration-150 ease-engineered ${
                  solid
                    ? "text-ink hover:bg-steel-900/5 active:bg-steel-900/5"
                    : "text-white hover:bg-white/10 active:bg-white/10"
                }`}
              >
                <Search size={30} strokeWidth={1.5} />
              </button>
              {isSearchOpen && (
                <input
                  autoFocus
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t("searchPlaceholder")}
                  className={`ml-2 w-full bg-transparent text-sm outline-none placeholder:text-steel-400 ${
                    solid ? "text-ink" : "text-white"
                  }`}
                />
              )}
            </form>
          </div>

          {/* Account icon — desktop only; on mobile it moves into the slide-out menu below. */}
          <div className="hidden lg:flex">
            <Link
              to={user?.role === "admin" ? "/admin" : user?.role === "customer" ? "/account" : "/login"}
              aria-label={
                user?.role === "admin"
                  ? t("adminPanel")
                  : user?.role === "customer"
                    ? t("accountPageTitle")
                    : t("login")
              }
              className={`flex h-12 w-12 items-center justify-center transition-colors duration-150 ease-engineered ${
                solid
                  ? "text-ink hover:bg-steel-900/5 active:bg-steel-900/5"
                  : "text-white hover:bg-white/10 active:bg-white/10"
              }`}
            >
              <User size={30} strokeWidth={1.5} />
            </Link>
          </div>

          {/* Cart — desktop version keeps the label, drops the frame. */}
          <button
            type="button"
            onClick={openDrawer}
            className={`hidden h-12 items-center gap-1 px-4 font-sans text-sm font-semibold transition-colors duration-150 ease-engineered lg:flex ${
              solid
                ? "text-ink hover:bg-steel-900/5 active:bg-steel-900/5"
                : "text-white hover:bg-white/10 active:bg-white/10"
            }`}
          >
            <span>{t("inProject")}</span>
            <span>({items.length})</span>
          </button>

          {/* Mobile: borderless cart + menu icons, generous tap targets, no boxes.
              Negative margin/spacing compensates for the buttons' own 9px of
              internal padding (h-12/w-12 box around a 30px icon) so the
              hamburger's glyph — not its larger invisible tap target — lines
              up with the container's right edge, and the cart icon sits
              closer to it than the tap-target boxes alone would allow. */}
          <div className="-mr-2 flex items-center lg:hidden">
            <button
              type="button"
              onClick={openDrawer}
              aria-label={t("inProject")}
              className={`relative flex h-12 w-12 items-center justify-center transition-colors duration-150 ease-engineered ${
                solid
                  ? "text-ink hover:bg-steel-900/5 active:bg-steel-900/5"
                  : "text-white hover:bg-white/10 active:bg-white/10"
              }`}
            >
              <ShoppingCart size={30} strokeWidth={1.5} className="-translate-x-[2px]" />
              {items.length > 0 && (
                <span className="absolute right-1 top-1 flex h-5 min-w-5 items-center justify-center bg-accent px-1 text-[11px] font-semibold leading-none text-white">
                  {items.length}
                </span>
              )}
            </button>

            <button
              type="button"
              aria-label={t("menuToggle")}
              onClick={() => setMobileOpen((v) => !v)}
              className={`flex h-12 w-12 items-center justify-center transition-colors duration-150 ease-engineered ${
                solid
                  ? "text-ink hover:bg-steel-900/5 active:bg-steel-900/5"
                  : "text-white hover:bg-white/10 active:bg-white/10"
              }`}
            >
              <MenuToggle open={isMobileOpen} size={30} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </div>

      {/* Same grid-template-rows fold used above for the top bar's
          scroll-collapse — 0fr/1fr animates to the content's natural
          height without a JS measurement step, so the panel folds open
          and closed instead of popping in/out with the old {isMobileOpen
          && <div>} conditional mount. */}
      <div
        className={`grid overflow-hidden border-steel-200 bg-white transition-[grid-template-rows] duration-300 ease-engineered lg:hidden ${
          isMobileOpen ? "grid-rows-[1fr] border-t" : "grid-rows-[0fr] border-t-0"
        }`}
      >
        <div className="overflow-hidden">
          <nav className="container-industrial flex flex-col py-4">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setMobileOpen(false)}
                className="border-b border-steel-100 py-3 text-sm font-semibold uppercase tracking-wide2 text-steel-700 last:border-none"
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/contacts"
              onClick={() => setMobileOpen(false)}
              className="border-b border-steel-100 py-3 text-sm font-semibold uppercase tracking-wide2 text-steel-700 last:border-none"
            >
              {t("contacts")}
            </Link>
            <div className="flex items-center justify-between pt-4">
              <Link
                to={user?.role === "admin" ? "/admin" : user?.role === "customer" ? "/account" : "/login"}
                onClick={() => setMobileOpen(false)}
                className="text-sm font-semibold uppercase tracking-wide2 text-steel-700"
              >
                {user?.role === "admin"
                  ? t("adminPanel")
                  : user?.role === "customer"
                    ? `${t("accountPageTitle")} (${user.name})`
                    : t("login")}
              </Link>
              <LanguagePicker solid textClassName="text-sm" />
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
}
